/**
 * Renaming one workspace item, shared by the inline field (desktop canvas
 * views and the sidebar tree) and the mobile dialog: the same calls and
 * checks for both: `patchProject(name)` for a folder, validated against the collection name
 * limits, and `patchArtifact(title)` for a file.
 */

import type { WorkspaceItem } from '@bower/shared';

import { apiService } from '../../../services/api.service';
import { patchArtifact } from '../../../services/api/artifacts.api';
import { patchProject } from '../../../services/api/projects.api';
import { validateField } from '../../../utils/validation';

/**
 * How much of the name to select when a rename starts, as Finder does: a
 * file's name up to its extension, so typing replaces the name and keeps
 * ".xyz"; the whole name for a collection, a note, or a file whose name does
 * not end in its recorded extension ("Assay v2.1" has none).
 */
export function baseNameEnd(item: WorkspaceItem): number {
  const ext = item.kind === 'file' ? item.fileExtension : null;
  const suffix = ext ? `.${ext.replace(/^\./, '')}` : null;
  const endsWithExt = suffix !== null && item.name.toLowerCase().endsWith(suffix.toLowerCase());
  return endsWithExt && item.name.length > suffix.length
    ? item.name.length - suffix.length
    : item.name.length;
}

export type RenameResult = { readonly ok: true } | { readonly ok: false; readonly error?: string };

/** Saves `name` (already trimmed, non-empty, changed) as the item's name. */
export async function renameItem(item: WorkspaceItem, name: string): Promise<RenameResult> {
  const nameError = item.kind === 'folder' ? validateField('project', 'name', name) : null;
  if (nameError) return { ok: false, error: nameError };
  const result =
    item.kind === 'folder'
      ? await patchProject(apiService, item.id, 'name', name).catch(() => null)
      : await patchArtifact(apiService, item.id, 'title', name).catch(() => null);
  return result?.success ? { ok: true } : { ok: false, error: result?.error?.message };
}
